import { signatureRequestsApi } from "@api";
import {
  RequirementType,
  SignatureRequest
} from "@wingspanhq/wingspan-api-sdk";
import { useWSQueries } from "@ws-react-query";
import { useMemo } from "react";

import { queriesSignatureRequests } from "../../../../v3/query/queriesSignatureRequests";
import { getDataSourceQueries } from "../../helpers/getDataSourceQueries";
import {
  RequirementTableCallback,
  RequirementTableItemData,
  RequirementTableRowConfig
} from "../../types";
import { buildConfigFields } from "../../utils";

import { getSignatureRequestFromCache } from "./configs/helpers/getSignatureRequestFromCache";
import { usePayeeSignatureRequirementConfig } from "./configs/payee/usePayeeSignatureRequirementConfig";
import { usePayerSignatureRequirementConfig } from "./configs/payer/usePayerSignatureRequirementConfig";

/**
 * Hoisted so the batch read and `buildConfigFields` address the same cache
 * entry — the config resolves a missing document's pending/failed state through
 * this key.
 */
const getDataSourceQueryKey = (id: string) =>
  queriesSignatureRequests.getSignatureRequest.getQueryKey({ requestId: id });

const useContextQueries = (requirements: RequirementTableItemData[]) => {
  const queries = getDataSourceQueries(
    getDataSourceQueryKey,
    requirements,
    id => signatureRequestsApi.getSignatureRequest({ requestId: id }),
    RequirementType.SIGNATURE
  );

  return useWSQueries({ queries });
};

export const useSignatureRequirementTableConfig = (
  callback: RequirementTableCallback,
  requirements: RequirementTableItemData[]
): RequirementTableRowConfig[RequirementType.SIGNATURE] => {
  const contextQueries = useContextQueries(requirements);

  const payerSignatureRequirementConfig =
    usePayerSignatureRequirementConfig(callback);
  const payeeSignatureRequirementConfig =
    usePayeeSignatureRequirementConfig(callback);

  const queriesHash = contextQueries
    .map(query => query.dataUpdatedAt)
    .join(",");

  // recompute only when the batch query data updates — the payer/payee configs
  // are new objects every render and must not invalidate the memo
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fields = useMemo(
    buildConfigFields<SignatureRequest, RequirementType.SIGNATURE>({
      payerConfig: payerSignatureRequirementConfig,
      payeeConfig: payeeSignatureRequirementConfig,
      getAdditionalData: getSignatureRequestFromCache,
      hasDataSource: true,
      getDataSourceQueryKey
    }),
    [queriesHash]
  );

  return {
    ...fields,
    getAdditionalData(requirement, context) {
      return getSignatureRequestFromCache(requirement, context);
    },
    isLoading: contextQueries.some(query => query.isLoading),
    isFetching: contextQueries.some(query => query.isFetching),
    dataUpdatedAt: queriesHash
  };
};
